import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Check, ArrowRight, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { plans, site } from "@/config/site";

/**
 * /get-started
 * Two-step conversion flow:
 *   1. Plan selection (pre-selects from ?plan= query if provided)
 *   2. Contact form → posts to /api/tickets → osTicket
 * Single-page scroll layout; the form auto-scrolls to view once a plan is chosen.
 */
export default function GetStarted() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const initialPlan = (params.get("plan") || "").toLowerCase();
  const [selectedPlan, setSelectedPlan] = useState(
    plans.find((p) => p.id === initialPlan)?.id || null
  );

  const [form, setForm] = useState({
    name: "",
    business: "",
    email: "",
    phone: "",
    city: "",
    computers: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {ok, ticket?, error?}

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (selectedPlan) {
      const el = document.getElementById("your-details");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedPlan]);

  const chosen = plans.find((p) => p.id === selectedPlan);

  async function onSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setResult(null);
    setSubmitting(true);
    try {
      const res = await fetch("/api/tickets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          business: form.business.trim() || null,
          email: form.email.trim(),
          phone: form.phone.trim(),
          city: form.city.trim() || null,
          computers: form.computers.trim() || null,
          plan: chosen ? chosen.name : null,
          message: form.message.trim() || null,
          source: "website",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setResult({ ok: true, ticket: data.ticketNumber });
        // scroll to result
        setTimeout(() => {
          document.getElementById("submit-result")?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      } else {
        setResult({
          ok: false,
          error:
            data.detail ||
            "We couldn't submit your request just now. Please try again or WhatsApp us.",
        });
      }
    } catch (err) {
      setResult({
        ok: false,
        error: "Network error. Please try again or WhatsApp us.",
      });
    } finally {
      setSubmitting(false);
    }
  }

  const inputCls =
    "mt-1.5 w-full rounded-md border border-hairline bg-white px-4 py-2.5 text-ink placeholder:text-ink-body/50 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand transition";

  // Success screen ----------------------------------------------------------
  if (result?.ok) {
    return (
      <section className="bg-white">
        <div className="mx-auto max-w-2xl px-5 lg:px-8 py-24 text-center">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-leaf-pill mb-6">
            <CheckCircle2 className="h-9 w-9 text-leaf-dark" strokeWidth={1.75} />
          </div>
          <div className="rule mx-auto" />
          <div className="eyebrow" style={{ marginBottom: "12px" }}>Request received</div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-ink leading-tight">
            Thank you — we've got your request.
          </h1>
          <p className="mt-4 text-lg text-ink-body leading-relaxed">
            Your enquiry has been logged as ticket{" "}
            <span className="font-mono font-semibold text-brand">
              #{result.ticket}
            </span>
            . Our team will call or email you within one business day —
            usually much sooner.
          </p>
          <div className="mt-8 rounded-card bg-brand-card p-6 text-left">
            <p className="text-sm text-ink-body leading-relaxed">
              <strong className="text-ink">What happens next:</strong>
              <br />
              <span className="block mt-2">
                → We'll reach out to confirm your setup and plan.
              </span>
              <span className="block mt-1">
                → We'll schedule the 15-minute installation on each computer.
              </span>
              <span className="block mt-1">
                → Backups start running automatically the same night.
              </span>
            </p>
          </div>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-md border border-brand text-brand hover:bg-brand hover:text-white px-6 py-3 font-semibold text-sm transition-colors"
              data-testid="thank-you-back-home"
            >
              Back to Home
            </Link>
            <Link
              to="/how-it-works"
              className="inline-flex items-center gap-2 text-brand hover:underline text-sm font-medium"
            >
              Read how it works <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    );
  }

  // Main flow ---------------------------------------------------------------
  return (
    <>
      {/* HERO */}
      <section
        className="text-white"
        style={{ background: "linear-gradient(105deg,#0F2B46 0%,#196CA3 100%)" }}
      >
        <div className="mx-auto max-w-site px-5 lg:px-8 py-14 lg:py-16">
          <div className="rule" style={{ background: "#4FC3F7" }} />
          <div className="eyebrow !text-brand-light">Get started</div>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight">
            Two steps and you're protected.
          </h1>
          <p className="mt-3 text-lg text-brand-mist max-w-2xl">
            Pick a plan. Tell us about your business. We'll get back to you within
            one business day.
          </p>
        </div>
      </section>

      {/* STEP 1 — PLAN SELECTION */}
      <section className="bg-white">
        <div className="mx-auto max-w-site px-5 lg:px-8 py-14">
          <div className="flex items-baseline gap-3">
            <span className="h-8 w-8 rounded-full bg-brand text-white font-bold text-sm flex items-center justify-center">1</span>
            <div>
              <div className="rule" />
              <div className="eyebrow">Choose a plan</div>
            </div>
          </div>
          <h2 className="mt-2 text-2xl md:text-3xl font-bold text-ink">
            Pick the size that fits your business.
          </h2>
          <p className="mt-2 text-ink-body">
            You can change this later — we'll confirm it with you before setup.
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {plans.map((p) => {
              const active = selectedPlan === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedPlan(p.id)}
                  className={`relative rounded-card p-5 text-left transition-all ${
                    active
                      ? "bg-white border-2 border-brand shadow-card-lift -translate-y-1"
                      : "bg-brand-card border-2 border-transparent hover:border-brand/40"
                  }`}
                  data-testid={`select-plan-${p.id}`}
                >
                  {p.badge && (
                    <span className="absolute -top-3 left-4 rounded-full bg-brand text-white text-[10px] font-bold px-2.5 py-1 uppercase tracking-wider">
                      {p.badge}
                    </span>
                  )}
                  {active && (
                    <span className="absolute -top-3 right-4 rounded-full bg-leaf text-white h-6 w-6 flex items-center justify-center">
                      <Check className="h-3.5 w-3.5" strokeWidth={3} />
                    </span>
                  )}
                  <h3 className="text-lg font-bold text-ink">{p.name}</h3>
                  <p className="mt-1 text-xs text-ink-body">{p.tagline}</p>
                  <div className="mt-3">
                    <span className="text-2xl font-extrabold text-ink">
                      ₹{p.price.toLocaleString("en-IN")}
                    </span>
                    <span className="text-xs text-ink-body"> /PC/mo</span>
                  </div>
                  <ul className="mt-3 space-y-1 text-xs text-ink-body">
                    <li>{p.storage} · {p.history} history</li>
                    <li>{p.support} support</li>
                  </ul>
                </button>
              );
            })}
          </div>

          {!selectedPlan && (
            <p className="mt-6 text-sm text-ink-body/80 italic">
              Not sure which plan is right? Pick <strong className="text-ink">Standard</strong> — it's the sweet spot for most businesses. We'll confirm the best fit for you before setup.
            </p>
          )}
        </div>
      </section>

      {/* STEP 2 — FORM */}
      <section id="your-details" style={{ backgroundColor: "#F8FAFC" }}>
        <div className="mx-auto max-w-3xl px-5 lg:px-8 py-14">
          <div className="flex items-baseline gap-3">
            <span
              className={`h-8 w-8 rounded-full font-bold text-sm flex items-center justify-center ${
                selectedPlan ? "bg-brand text-white" : "bg-hairline text-ink-body"
              }`}
            >
              2
            </span>
            <div>
              <div className="rule" />
              <div className="eyebrow">Your details</div>
            </div>
          </div>
          <h2 className="mt-2 text-2xl md:text-3xl font-bold text-ink">
            Tell us about your business.
          </h2>
          {chosen ? (
            <p className="mt-2 text-ink-body">
              Selected plan:{" "}
              <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-card text-brand px-3 py-0.5 font-semibold text-sm">
                <Check className="h-3.5 w-3.5" strokeWidth={2.6} />
                {chosen.name} — ₹{chosen.price.toLocaleString("en-IN")}/PC/mo
              </span>
            </p>
          ) : (
            <p className="mt-2 text-ink-body">
              Fill this in and we'll match you to the right plan.
            </p>
          )}

          <form
            onSubmit={onSubmit}
            className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4"
            data-testid="get-started-form"
            noValidate
          >
            <div>
              <label className="text-sm font-medium text-ink">Your name *</label>
              <input required value={form.name} onChange={upd("name")} className={inputCls} placeholder="Ravi Sharma" data-testid="gs-name" />
            </div>
            <div>
              <label className="text-sm font-medium text-ink">Business name</label>
              <input value={form.business} onChange={upd("business")} className={inputCls} placeholder="Sharma & Co CA" data-testid="gs-business" />
            </div>
            <div>
              <label className="text-sm font-medium text-ink">Email *</label>
              <input required type="email" value={form.email} onChange={upd("email")} className={inputCls} placeholder="you@example.com" data-testid="gs-email" />
            </div>
            <div>
              <label className="text-sm font-medium text-ink">Phone *</label>
              <input required type="tel" value={form.phone} onChange={upd("phone")} className={inputCls} placeholder="+91 98XXX XXXXX" data-testid="gs-phone" />
            </div>
            <div>
              <label className="text-sm font-medium text-ink">City</label>
              <input value={form.city} onChange={upd("city")} className={inputCls} placeholder="Mumbai" data-testid="gs-city" />
            </div>
            <div>
              <label className="text-sm font-medium text-ink">How many computers?</label>
              <input value={form.computers} onChange={upd("computers")} className={inputCls} placeholder="e.g. 3" data-testid="gs-computers" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-ink">Anything else? (optional)</label>
              <textarea rows={4} value={form.message} onChange={upd("message")} className={`${inputCls} resize-none`} placeholder="Special requirements, folders you know you want backed up, timeline…" data-testid="gs-message" />
            </div>

            {result && !result.ok && (
              <div id="submit-result" className="md:col-span-2 rounded-card p-4 flex items-start gap-3" style={{ background: "#FEF2F2", borderLeft: "4px solid #DC2626" }}>
                <AlertCircle className="h-5 w-5 text-danger mt-0.5 shrink-0" strokeWidth={1.75} />
                <div className="text-sm text-ink-body leading-relaxed">
                  <strong className="text-danger">Something went wrong.</strong>
                  <div className="mt-1">{result.error}</div>
                </div>
              </div>
            )}

            <div className="md:col-span-2 mt-2 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-md text-white px-8 py-3.5 text-base font-bold shadow-card-soft hover:shadow-card-lift hover:-translate-y-px transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: "linear-gradient(100deg,#1D6C35 0%,#2C994D 100%)" }}
                data-testid="gs-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Submitting…
                  </>
                ) : (
                  <>
                    Submit &amp; Get Protected
                    <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
                  </>
                )}
              </button>
              <p className="text-xs text-ink-body/70 max-w-sm">
                By submitting, you agree to our{" "}
                <Link to="/privacy" className="text-brand underline hover:no-underline">Privacy Policy</Link>
                {" "}and{" "}
                <Link to="/terms" className="text-brand underline hover:no-underline">Terms</Link>.
                We reply within one business day.
              </p>
            </div>
          </form>
        </div>
      </section>

      {/* REASSURANCE */}
      <section className="bg-white">
        <div className="mx-auto max-w-site px-5 lg:px-8 py-14">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { t: "No card required now", b: "We only take payment after you approve the plan." },
              { t: "Setup in one visit", b: "15 minutes per computer, remotely or in-person." },
              { t: "Prefer to talk first?", b: <>Call <a href={`tel:${site.phoneTel}`} className="text-brand underline hover:no-underline">{site.phoneDisplay}</a> — we're on {site.businessHours}.</> },
            ].map((c, i) => (
              <div key={i} className="rounded-card bg-brand-card p-5">
                <h3 className="text-sm font-bold text-ink">{c.t}</h3>
                <p className="mt-1 text-sm text-ink-body leading-relaxed">{c.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
